import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../firebase/config';
import { DATA_KEY_TO_COLLECTION } from '../firebase/collections';
import { PUBLIC_DATA_KEYS, getSubscribedDataKeysForUser } from '../firebase/dataSubscriptionKeys';
import { snapDocsToArray, commitDataKey, commitDataKeys } from '../services/firestoreSync';
import { useAuth } from './AuthContext';

const AppContext = createContext();

export { PUBLIC_DATA_KEYS } from '../firebase/dataSubscriptionKeys';

const INITIAL_DATA = {
  projects: [],
  employees: [],
  clients: [],
  contacts: [],
  invoices: [],
  quotations: [],
  jobs: [],
  applicants: [],
  meetings: [],
  portfolio: [],
  attendance: [],
  tasks: [],
  leaveRequests: [],
  approvalRequests: [],
  notifications: [],
  websiteServices: [],
  websiteNews: [],
  websitePartners: [],
  documents: [],
};

export function AppProvider({ children }) {
  const { user, authReady } = useAuth();
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const [toast, setToast] = useState(null);
  const [listenerGeneration, setListenerGeneration] = useState(0);
  const seenKeys = useRef({});

  const retryDataSync = useCallback(() => {
    setDataError(null);
    setLoading(true);
    setListenerGeneration((g) => g + 1);
  }, []);

  useEffect(() => {
    if (!authReady) return undefined;

    const keys = getSubscribedDataKeysForUser(user);

    if (!user) {
      setData((prev) => {
        const next = { ...INITIAL_DATA };
        PUBLIC_DATA_KEYS.forEach((k) => {
          next[k] = prev[k] || [];
        });
        return next;
      });
    } else {
      setData((prev) => {
        const next = { ...INITIAL_DATA };
        keys.forEach((k) => {
          next[k] = Array.isArray(prev[k]) ? prev[k] : [];
        });
        return next;
      });
    }

    setDataError(null);
    setLoading(true);
    seenKeys.current = Object.fromEntries(keys.map((k) => [k, false]));

    const db = getFirebaseDb();
    const unsubs = keys.map((key) => {
      const colName = DATA_KEY_TO_COLLECTION[key];
      return onSnapshot(
        collection(db, colName),
        (snap) => {
          const rows = snapDocsToArray(snap);
          setData((prev) => ({ ...prev, [key]: rows }));
          seenKeys.current[key] = true;
          if (keys.every((k) => seenKeys.current[k])) setLoading(false);
        },
        (err) => {
          setDataError(err.message || 'Firestore listener error');
          setLoading(false);
        }
      );
    });

    return () => unsubs.forEach((u) => u());
  }, [authReady, user?.uid, user?.role, listenerGeneration]);

  const addNotification = useCallback((text, type = 'general', forRoles = []) => {
    const notif = {
      id: String(Date.now()),
      text,
      time: 'Just now',
      read: false,
      type,
      forRoles,
    };
    setData((prev) => {
      const next = [notif, ...prev.notifications];
      commitDataKey('notifications', next).catch(() => {});
      return { ...prev, notifications: next };
    });
  }, []);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type, id: Date.now() });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const commitData = useCallback(
    async (key, items) => {
      try {
        await commitDataKey(key, items);
      } catch (e) {
        showToast(e.message || 'Save failed', 'error');
        throw e;
      }
    },
    [showToast]
  );

  const commitMany = useCallback(
    async (partial) => {
      try {
        await commitDataKeys(partial);
      } catch (e) {
        showToast(e.message || 'Save failed', 'error');
        throw e;
      }
    },
    [showToast]
  );

  const setNotifications = useCallback((updater) => {
    setData((prev) => {
      const next = typeof updater === 'function' ? updater(prev.notifications) : updater;
      commitDataKey('notifications', next).catch(() => {});
      return { ...prev, notifications: next };
    });
  }, []);

  const getEmployee = useCallback(
    (id) => data.employees.find((e) => String(e.id) === String(id)),
    [data.employees]
  );

  const getEmployeeName = useCallback(
    (id) => data.employees.find((e) => String(e.id) === String(id))?.name || 'Unknown',
    [data.employees]
  );

  const value = useMemo(
    () => ({
      data,
      loading,
      dataError,
      retryDataSync,
      commitData,
      commitMany,
      notifications: data.notifications,
      setNotifications,
      addNotification,
      toast,
      showToast,
      getEmployee,
      getEmployeeName,
    }),
    [
      data,
      loading,
      dataError,
      retryDataSync,
      commitData,
      commitMany,
      setNotifications,
      addNotification,
      toast,
      showToast,
      getEmployee,
      getEmployeeName,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => useContext(AppContext);
